import { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import clsx from 'clsx';
import toast from 'react-hot-toast';
import {
  Image as ImageIcon, Type, Loader2, Download, Trash2, Copy as CopyIcon,
  Wand2, AlertCircle, Clock, Film, Mic, Share2, LayoutTemplate, Send, Link as LinkIcon, Play, X, ListVideo,
} from 'lucide-react';
import { Topbar } from '@/components/Topbar';
import { Card, SectionHeader } from '@/components/Card';
import { Button } from '@/components/Button';
import { Textarea } from '@/components/Textarea';
import { Select } from '@/components/Select';
import { EmptyState } from '@/components/EmptyState';
import { Pagination } from '@/components/Pagination';
import { ConfirmDialog } from '@/components/ConfirmDialog';
import { EnhancePromptButton } from '@/components/EnhancePromptButton';
import { MediaUpload } from '@/components/MediaUpload';
import {
  aiStudioService, AiGeneration, AI_STUDIO_PRESETS,
} from '@/services/ai-studio.service';
import { mergeVideoAudio, concatClips, extractLastFrame } from '@/lib/ffmpegStudio';
import { settingsService, SavedVoice } from '@/services/settings.service';

type CreateKind = 'image' | 'video' | 'audio' | 'copy';

const POLL_INTERVAL_MS = 3_000;
const MAX_POLLS = 40; // ~2 minutes (image / audio)
const MAX_POLLS_VIDEO = 140; // ~7 minutes — Veo renders for several minutes

const KIND_META: Record<CreateKind, { label: string; noun: string; historyLabel: string; ext: string }> = {
  image: { label: 'Image',     noun: 'image',     historyLabel: 'Images',     ext: 'png' },
  video: { label: 'Video',     noun: 'video',     historyLabel: 'Videos',     ext: 'mp4' },
  audio: { label: 'Voiceover', noun: 'voiceover', historyLabel: 'Voiceovers', ext: 'mp3' },
  copy:  { label: 'Copy',      noun: 'copy',      historyLabel: 'Copy',       ext: 'txt' },
};

/** Cross-origin ImageKit URLs ignore the `download` attribute — go via blob. */
async function downloadAsset(url: string, fileName: string) {
  try {
    const res = await fetch(url);
    if (!res.ok) throw new Error(`fetch failed (${res.status})`);
    const blob = await res.blob();
    const objectUrl = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = objectUrl;
    a.download = fileName;
    document.body.appendChild(a);
    a.click();
    a.remove();
    URL.revokeObjectURL(objectUrl);
  } catch {
    toast.error('Download failed — try opening the asset in a new tab.');
  }
}

function copyToClipboard(text: string, message = 'Copied to clipboard') {
  navigator.clipboard.writeText(text)
    .then(() => toast.success(message))
    .catch(() => toast.error('Copy failed'));
}

const PRESET_LABELS: Record<string, string> = Object.fromEntries(
  AI_STUDIO_PRESETS.map((p) => [p.key, p.label]),
);

export default function AiStudio() {
  const nav = useNavigate();
  const [kind, setKind] = useState<CreateKind>('image');
  const [prompt, setPrompt] = useState('');
  const [preset, setPreset] = useState<string | null>('ig_post');
  const [generating, setGenerating] = useState(false);
  const [result, setResult] = useState<AiGeneration | null>(null);
  const [config, setConfig] = useState<{ elevenLabsConfigured: boolean } | null>(null);

  // Model selection (image + video). Populated from the live model list; the
  // selected id is sent with the generation request.
  const [models, setModels] = useState<{
    image: Array<{ id: string; label: string; default: boolean }>;
    video: Array<{ id: string; label: string; default: boolean }>;
  } | null>(null);
  const [imageModel, setImageModel] = useState<string>('');
  const [videoModel, setVideoModel] = useState<string>('');
  // Image-to-video: Veo animates the clip FROM this frame, so real product
  // shots (an app screen, a dish) stay accurate instead of being reinvented.
  const [sourceImageUrl, setSourceImageUrl] = useState('');
  // Voiceover baked into the video pipeline: generate clip → generate speech →
  // merge in the browser → save the finished video. One flow, one result.
  const [includeVoice, setIncludeVoice] = useState(false);
  // 'auto' = AI writes the narration from the video prompt, so the words match
  // whatever the clip turns out to show; 'script' = spoken verbatim.
  const [voiceMode, setVoiceMode] = useState<'auto' | 'script'>('auto');
  const [voiceScript, setVoiceScript] = useState('');
  // Long videos: each Veo clip is ~8s, so length is built by CHAINING — the
  // next clip starts from the previous clip's last frame, then the two are
  // joined. Repeat to reach 16s, 24s, 32s…
  const [continueFrom, setContinueFrom] = useState<AiGeneration | null>(null);
  const [recentVideos, setRecentVideos] = useState<AiGeneration[] | null>(null);
  const [showContinuePicker, setShowContinuePicker] = useState(false);
  const [extractingFrame, setExtractingFrame] = useState(false);
  const [postStage, setPostStage] = useState<string | null>(null);

  // Voiceover voices (from Settings → AI). User picks one per voiceover.
  const [savedVoices, setSavedVoices] = useState<SavedVoice[]>([]);
  const [voiceId, setVoiceId] = useState<string>('');
  const voicePreviewRef = useRef<HTMLAudioElement | null>(null);

  // History
  const [historyKind, setHistoryKind] = useState<CreateKind>('image');
  const [history, setHistory] = useState<{ items: AiGeneration[]; total: number } | null>(null);
  const [page, setPage] = useState(1);
  const [del, setDel] = useState<AiGeneration | null>(null);
  const [useMenuFor, setUseMenuFor] = useState<string | null>(null);
  const HISTORY_LIMIT = 12;

  const pollCancelled = useRef(false);
  useEffect(() => () => { pollCancelled.current = true; }, []);

  useEffect(() => {
    aiStudioService.getConfig().then(setConfig).catch(() => setConfig({ elevenLabsConfigured: false }));
    settingsService.getElevenLabs()
      .then((r) => {
        setSavedVoices(r.voices ?? []);
        setVoiceId(r.voiceId ?? r.voices?.[0]?.voiceId ?? '');
      })
      .catch(() => setSavedVoices([]));
    aiStudioService.listModels()
      .then((m) => {
        setModels(m);
        setImageModel(m.image.find((x) => x.default)?.id ?? m.image[0]?.id ?? '');
        setVideoModel(m.video.find((x) => x.default)?.id ?? m.video[0]?.id ?? '');
      })
      .catch(() => setModels({ image: [], video: [] }));
  }, []);

  const refreshHistory = useCallback(() => {
    aiStudioService.list({ kind: historyKind, page, limit: HISTORY_LIMIT })
      .then((r) => setHistory({ items: r.items, total: r.total }))
      .catch(() => setHistory({ items: [], total: 0 }));
  }, [historyKind, page]);

  useEffect(() => { refreshHistory(); }, [refreshHistory]);

  const switchKind = (k: CreateKind) => {
    setKind(k);
    setResult(null);
    const first = AI_STUDIO_PRESETS.find((p) => p.kind === k);
    setPreset(first?.key ?? null);
    setHistoryKind(k);
    setPage(1);
  };

  const finishGeneration = (g: AiGeneration) => {
    setGenerating(false);
    setResult(g);
    if (g.status === 'succeeded') {
      toast.success(`${KIND_META[g.kind as CreateKind].label} generated.`);
    } else {
      toast.error(g.error ?? 'Generation failed.');
    }
    refreshHistory();
  };

  /** Pick a library video to continue from: seed the next clip with its last frame. */
  const chooseContinueFrom = async (v: AiGeneration) => {
    if (!v.assetUrl) return;
    setExtractingFrame(true);
    try {
      const frame = await extractLastFrame(v.assetUrl);
      const uploaded = await aiStudioService.uploadMedia(
        new File([frame], 'frame.jpg', { type: 'image/jpeg' }),
        `Frame — ${(v.title || v.prompt || 'video').slice(0, 40)}`,
      );
      if (!uploaded.assetUrl) throw new Error('frame upload failed');
      setContinueFrom(v);
      setSourceImageUrl(uploaded.assetUrl);
      setShowContinuePicker(false);
      toast.success('Next clip will start exactly where that video ends.');
    } catch (e) {
      toast.error(`Could not read the video's last frame: ${(e as Error).message}`);
    } finally { setExtractingFrame(false); }
  };

  const clearContinueFrom = () => {
    setContinueFrom(null);
    setSourceImageUrl('');
  };

  const openContinuePicker = () => {
    setShowContinuePicker((v) => !v);
    if (recentVideos === null) {
      aiStudioService.list({ mediaType: 'video', mediaOnly: true, limit: 20 })
        .then((r) => setRecentVideos(r.items))
        .catch(() => setRecentVideos([]));
    }
  };

  /**
   * After the raw clip succeeds: join it onto the previous clip (long-video
   * chaining) and/or lay the voiceover over it — all in the browser — then
   * save the finished video to the library as the result the user sees.
   */
  const postProcessVideo = async (g: AiGeneration): Promise<AiGeneration> => {
    let videoBlob: Blob | null = null;
    let audioUid: string | undefined;
    try {
      if (continueFrom?.assetUrl && g.assetUrl) {
        setPostStage('Joining the clips into one video…');
        videoBlob = await concatClips([continueFrom.assetUrl, g.assetUrl]);
      }
      if (includeVoice && (voiceMode === 'auto' || voiceScript.trim())) {
        const totalSec = (g.durationSec ?? 8) + (continueFrom ? (continueFrom.durationSec ?? 8) : 0);
        let script = voiceScript.trim();
        if (voiceMode === 'auto') {
          setPostStage('Writing the voiceover script…');
          // Reuse the copy generator so usage/cost are tracked like any other
          // generation. ~2.3 words/sec keeps the read inside the clip.
          const maxWords = Math.max(12, Math.round(totalSec * 2.3));
          const copy = await aiStudioService.create({
            kind: 'copy',
            prompt: `Write ONLY the spoken words for a ${totalSec}-second video voiceover — a single short paragraph, maximum ${maxWords} words. No hashtags, no emojis, no quotation marks, no stage directions, no "Voiceover:" prefix. Natural, confident ad narration. The video shows: ${g.prompt}`,
          });
          script = (copy.copyText ?? '').trim().replace(/^["'“”]+|["'“”]+$/g, '');
          if (!script) throw new Error('could not write a voiceover script — try Paste-my-text mode');
        }
        setPostStage('Generating the voiceover…');
        const audio = await aiStudioService.create({
          kind: 'audio', prompt: script, voiceId: voiceId || undefined,
        });
        if (audio.status !== 'succeeded' || !audio.assetUrl) {
          throw new Error(audio.error ?? 'voiceover generation failed');
        }
        audioUid = audio.generationUid;
        setPostStage('Merging the voiceover into the video…');
        const src = videoBlob ? URL.createObjectURL(videoBlob) : g.assetUrl!;
        try { videoBlob = await mergeVideoAudio(src, audio.assetUrl); }
        finally { if (src.startsWith('blob:')) URL.revokeObjectURL(src); }
      }
      if (!videoBlob) return g;

      setPostStage('Saving the finished video…');
      const durationSec = (g.durationSec ?? 8) + (continueFrom ? (continueFrom.durationSec ?? 8) : 0);
      const saved = await aiStudioService.saveCombined(videoBlob, {
        sourceVideoUid: g.generationUid,
        sourceAudioUid: audioUid,
        durationSec,
        title: `${continueFrom ? 'Extended' : 'Voiced'} — ${(g.prompt || 'video').slice(0, 40)}`,
      });
      // The chain continues from the new, longer video — one click to add 8s more.
      setContinueFrom(null);
      setSourceImageUrl('');
      return saved;
    } finally { setPostStage(null); }
  };

  const generate = async () => {
    const text = prompt.trim();
    if (!text) { toast.error(kind === 'audio' ? 'Write the voiceover script first.' : 'Write a prompt first.'); return; }
    if (kind === 'video' && includeVoice && voiceMode === 'script' && !voiceScript.trim()) {
      toast.error('Paste the voiceover text, or switch to "Let AI write it".'); return;
    }
    setGenerating(true);
    setResult(null);
    try {
      const created = await aiStudioService.create({
        kind,
        prompt: text,
        platformPreset: preset ?? undefined,
        model: kind === 'image' ? (imageModel || undefined) : kind === 'video' ? (videoModel || undefined) : undefined,
        voiceId: kind === 'audio' ? (voiceId || undefined) : undefined,
        sourceImageUrl: kind === 'video' ? (sourceImageUrl || undefined) : undefined,
      });

      if (created.status === 'succeeded' || created.status === 'failed') {
        finishGeneration(created);
        return;
      }

      // Async jobs run server-side — poll until terminal status. Video gets a
      // much longer window (Veo renders for minutes).
      const maxPolls = created.kind === 'video' ? MAX_POLLS_VIDEO : MAX_POLLS;
      let consecutiveErrors = 0;
      for (let i = 0; i < maxPolls; i++) {
        await new Promise((r) => setTimeout(r, POLL_INTERVAL_MS));
        if (pollCancelled.current) return;
        let g: AiGeneration;
        try {
          g = await aiStudioService.get(created.generationUid);
          consecutiveErrors = 0;
        } catch {
          // Server unreachable (e.g. backend restarting). Don't spin forever —
          // after ~30s of failed polls, stop and tell the user it's still
          // rendering server-side and will appear in History.
          if (++consecutiveErrors >= 10) {
            toast.error('Lost connection to the server. Your generation is still running — it will appear in History once you reconnect.');
            setGenerating(false);
            refreshHistory();
            return;
          }
          continue;
        }
        if (g.status === 'succeeded' || g.status === 'failed') {
          let final = g;
          if (g.kind === 'video' && g.status === 'succeeded' && (includeVoice || continueFrom)) {
            try {
              final = await postProcessVideo(g);
            } catch (e) {
              toast.error(`Finishing failed: ${(e as Error).message} — the raw clip is still in your library.`);
            }
          }
          finishGeneration(final);
          return;
        }
      }
      toast.error('Generation is taking longer than expected — check the history below in a minute.');
      setGenerating(false);
      refreshHistory();
    } catch (e: unknown) {
      const err = e as { response?: unknown };
      if (!err?.response) toast.error('Generation failed.');
      setGenerating(false);
      refreshHistory();
    }
  };

  const onDelete = async () => {
    if (!del) return;
    try {
      const r = await aiStudioService.remove(del.generationUid);
      if (r.imagekitDeleted === false) {
        toast('Removed — but the file may still be on ImageKit (delete failed).', { icon: '⚠️' });
      } else {
        toast.success('Deleted from library and ImageKit');
      }
      setDel(null);
      if (result?.generationUid === del.generationUid) setResult(null);
      refreshHistory();
    } catch { /* interceptor toasted */ }
  };

  /** "Use this asset" targets for succeeded image/video generations. */
  const applyToTemplate = (g: AiGeneration) => {
    const param = g.kind === 'video' ? 'headerVideoUrl' : 'headerImageUrl';
    nav(`/templates/new?${param}=${encodeURIComponent(g.assetUrl!)}`);
  };
  const applyToBroadcast = (g: AiGeneration) => {
    // Broadcasts have no media slot (template or plain text only) — copy the
    // URL so the user can paste it into the message, then open the composer.
    copyToClipboard(g.assetUrl!, 'Asset URL copied — paste it into your broadcast message');
    nav('/whatsapp-account/bulk/new');
  };

  const presets = AI_STUDIO_PRESETS.filter((p) => p.kind === kind);
  const elevenLabsMissing = config !== null && !config.elevenLabsConfigured;

  const promptPlaceholder: Record<CreateKind, string> = {
    image: 'Describe the image — e.g. "Fresh saffron latte on a marble table, warm morning light, Dubai cafe vibes"',
    video: 'Describe the video — e.g. "Slow pan over a sizzling shawarma grill, steam rising, golden hour, street-food energy" (clips are ~8 seconds)',
    audio: 'Write the exact script to be spoken — e.g. "Welcome to Bayt Al Qahwa! This week only: buy one latte, get one free."',
    copy: 'Describe what to write — e.g. "Announce our Ramadan iftar set menu, family-friendly, AED 99 per person"',
  };

  return (
    <>
      <Topbar
        title="AI Studio"
        breadcrumb="AI Studio / Create"
        description="Generate on-brand images, videos, voiceovers, and social copy for any platform."
      />

      <div className="mx-auto w-full max-w-[1500px] space-y-6 px-4 py-6 sm:px-6">
        <div className="grid gap-6 lg:grid-cols-2 lg:items-start">
        {/* Composer */}
        <Card>
          {/* Kind tabs */}
          <div className="mb-4 flex gap-1 rounded-lg bg-surface-alt p-1 w-fit">
            {([
              { k: 'image' as const, label: 'Image', icon: <ImageIcon size={14} /> },
              { k: 'video' as const, label: 'Video', icon: <Film size={14} /> },
              { k: 'audio' as const, label: 'Voiceover', icon: <Mic size={14} /> },
              { k: 'copy' as const, label: 'Copy', icon: <Type size={14} /> },
            ]).map((t) => {
              const disabled = t.k === 'audio' && elevenLabsMissing;
              return (
                <button
                  key={t.k}
                  onClick={() => !disabled && switchKind(t.k)}
                  disabled={disabled}
                  title={disabled ? 'Add your ElevenLabs API key in Settings → AI to enable voiceover' : undefined}
                  className={clsx(
                    'flex items-center gap-1.5 rounded-md px-4 py-1.5 text-[13px] font-semibold transition-colors',
                    kind === t.k
                      ? 'bg-surface text-text-primary shadow-soft'
                      : 'text-text-muted hover:text-text-primary',
                    disabled && 'cursor-not-allowed opacity-50 hover:text-text-muted',
                  )}
                >
                  {t.icon}{t.label}
                </button>
              );
            })}
          </div>

          {kind === 'audio' && elevenLabsMissing && (
            <div className="mb-3 rounded-lg border border-warning/40 bg-warning-soft px-3 py-2 text-xs text-text-secondary">
              Voiceover needs an ElevenLabs API key — add it in Settings → AI.
            </div>
          )}

          {/* Model selector — image + video only (copy uses the company default; audio is ElevenLabs) */}
          {(kind === 'image' || kind === 'video') && models && (
            <div className="mb-3">
              <label className="mb-1 block text-2xs font-medium uppercase tracking-wide text-text-muted">Model</label>
              {(kind === 'image' ? models.image : models.video).length === 0 ? (
                <p className="text-xs text-text-muted">Using the default model (couldn't list models — check your Gemini key in Settings → AI).</p>
              ) : (
                <Select
                  value={kind === 'image' ? imageModel : videoModel}
                  onChange={(e) => (kind === 'image' ? setImageModel(e.target.value) : setVideoModel(e.target.value))}
                  className="max-w-md"
                >
                  {(kind === 'image' ? models.image : models.video).map((m) => (
                    <option key={m.id} value={m.id}>{m.label}</option>
                  ))}
                </Select>
              )}
            </div>
          )}

          {/* Voice selector — voiceover tab only */}
          {kind === 'audio' && (
            <div className="mb-3">
              <label className="mb-1 block text-2xs font-medium uppercase tracking-wide text-text-muted">Voice</label>
              {savedVoices.length === 0 ? (
                <p className="rounded-lg border border-dashed border-border bg-surface-alt/40 px-3 py-2.5 text-xs text-text-muted">
                  No voices set up yet. Go to <a href="/settings/ai" className="font-medium text-primary hover:underline">Settings → AI</a> and pick your ElevenLabs voices — then choose one here.
                </p>
              ) : (
                <div className="flex items-center gap-2">
                  <Select value={voiceId} onChange={(e) => setVoiceId(e.target.value)} className="max-w-md">
                    {savedVoices.map((v) => (
                      <option key={v.voiceId} value={v.voiceId}>{v.name}{v.accent ? ` — ${v.accent}` : ''}</option>
                    ))}
                  </Select>
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    onClick={() => {
                      const v = savedVoices.find((x) => x.voiceId === voiceId);
                      if (!v?.previewUrl) { toast('No preview for this voice', { icon: '🔇' }); return; }
                      voicePreviewRef.current?.pause();
                      const a = new Audio(v.previewUrl);
                      voicePreviewRef.current = a;
                      a.play().catch(() => undefined);
                    }}
                    iconLeft={<Mic size={13} />}
                  >
                    Preview
                  </Button>
                </div>
              )}
            </div>
          )}

          {/* Image-to-video: optional source frame. The clip opens on exactly
              this image and animates from it — the way to get a REAL app
              screen or product shot into the video instead of an invented one. */}
          {kind === 'video' && (
            <div className="mb-3 space-y-3">
              {/* Long videos = chaining: the next clip starts on the previous
                  clip's last frame, then both are joined into one file. */}
              <div>
                <label className="mb-1 block text-2xs font-medium uppercase tracking-wide text-text-muted">
                  Continue from a video <span className="normal-case text-text-muted/70">(optional — extends it by ~8s)</span>
                </label>
                {continueFrom ? (
                  <div className="flex items-center gap-2 rounded-lg border border-primary/40 bg-primary-soft/40 px-3 py-2">
                    <Film size={14} className="shrink-0 text-primary" />
                    <span className="min-w-0 flex-1 truncate text-xs text-text-primary">
                      {continueFrom.title || continueFrom.prompt}
                    </span>
                    <span className="shrink-0 text-2xs text-text-muted">
                      ~{(continueFrom.durationSec ?? 8) + 8}s after this clip
                    </span>
                    <button onClick={clearContinueFrom} className="shrink-0 rounded p-0.5 text-text-muted hover:text-danger" title="Don't extend">
                      <X size={13} />
                    </button>
                  </div>
                ) : (
                  <Button variant="secondary" size="sm" onClick={openContinuePicker} loading={extractingFrame} iconLeft={<ListVideo size={13} />}>
                    Pick a video to extend
                  </Button>
                )}
                {showContinuePicker && !continueFrom && (
                  <div className="mt-2 max-h-52 space-y-1.5 overflow-y-auto rounded-lg border border-border p-2">
                    {recentVideos === null ? (
                      <div className="flex items-center gap-2 px-2 py-3 text-xs text-text-muted"><Loader2 size={13} className="animate-spin" /> Loading your videos…</div>
                    ) : recentVideos.length === 0 ? (
                      <p className="px-2 py-3 text-xs text-text-muted">No videos yet — generate the first clip, then extend it from here.</p>
                    ) : recentVideos.map((v) => (
                      <button
                        key={v.generationUid}
                        onClick={() => chooseContinueFrom(v)}
                        disabled={extractingFrame}
                        className="flex w-full items-center gap-2 rounded-lg border border-border px-2.5 py-2 text-left transition-colors hover:bg-surface-alt"
                      >
                        <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded bg-black/80"><Play size={12} className="text-white/80" /></span>
                        <span className="min-w-0 flex-1 truncate text-xs text-text-primary">{v.title || v.prompt}</span>
                        <span className="shrink-0 text-2xs text-text-muted">{v.durationSec ?? 8}s</span>
                      </button>
                    ))}
                  </div>
                )}
              </div>

              {/* One start frame only — Veo animates FROM a single image. For a
                  video that walks through several screenshots, generate one clip
                  per screenshot and chain them with "Continue from a video". */}
              {continueFrom ? (
                sourceImageUrl && (
                  <div className="flex items-center gap-2 rounded-lg border border-border bg-surface-alt/40 px-3 py-2">
                    <img src={sourceImageUrl} alt="Continuation frame" className="h-10 w-10 rounded object-cover" />
                    <p className="text-2xs text-text-muted">
                      The new clip starts on the last frame of the chosen video, so the join is seamless.
                    </p>
                  </div>
                )
              ) : (
                <div>
                  <label className="mb-1 block text-2xs font-medium uppercase tracking-wide text-text-muted">
                    Start from an image <span className="normal-case text-text-muted/70">(optional — one frame; the video opens on it)</span>
                  </label>
                  <MediaUpload
                    mode="image"
                    value={sourceImageUrl}
                    folder="ai-studio/frames"
                    onChange={(url) => setSourceImageUrl(url)}
                  />
                  {sourceImageUrl && (
                    <p className="mt-1 text-2xs text-text-muted">
                      The video opens on this exact frame and animates from it — one image per clip. To feature several
                      screenshots, generate a clip per screenshot and chain them with “Continue from a video”.
                    </p>
                  )}
                </div>
              )}

              {/* Voiceover baked into the same flow — no separate combine step. */}
              <div className="rounded-lg border border-border bg-surface-alt/30 p-3">
                <label className={clsx('flex items-center gap-2.5', elevenLabsMissing ? 'cursor-not-allowed opacity-60' : 'cursor-pointer')}>
                  <input
                    type="checkbox"
                    checked={includeVoice}
                    disabled={elevenLabsMissing || generating}
                    onChange={(e) => setIncludeVoice(e.target.checked)}
                    className="h-4 w-4 rounded accent-primary"
                  />
                  <span className="text-sm font-medium text-text-primary">Include voiceover</span>
                  <span className="ml-auto text-2xs text-text-muted">
                    {elevenLabsMissing ? 'Needs ElevenLabs — Settings → AI' : 'Spoken over the finished video'}
                  </span>
                </label>
                {includeVoice && !elevenLabsMissing && (
                  <div className="mt-3 space-y-2">
                    {/* You can't script a video you haven't seen — auto mode
                        writes the narration FROM the same prompt, after the
                        clip is rendered, so words and picture stay in step. */}
                    <div className="flex gap-1 rounded-lg bg-surface-alt p-1 w-fit">
                      {([
                        { m: 'auto' as const, label: 'Let AI write it' },
                        { m: 'script' as const, label: 'Paste my text' },
                      ]).map((t) => (
                        <button
                          key={t.m}
                          type="button"
                          onClick={() => setVoiceMode(t.m)}
                          disabled={generating}
                          className={clsx(
                            'rounded-md px-3 py-1 text-xs font-semibold transition-colors',
                            voiceMode === t.m ? 'bg-surface text-text-primary shadow-soft' : 'text-text-muted hover:text-text-primary',
                          )}
                        >
                          {t.label}
                        </button>
                      ))}
                    </div>
                    {savedVoices.length > 0 && (
                      <Select value={voiceId} onChange={(e) => setVoiceId(e.target.value)} className="max-w-md">
                        {savedVoices.map((v) => (
                          <option key={v.voiceId} value={v.voiceId}>{v.name}{v.accent ? ` — ${v.accent}` : ''}</option>
                        ))}
                      </Select>
                    )}
                    {voiceMode === 'script' ? (
                      <>
                        <Textarea
                          rows={2}
                          value={voiceScript}
                          onChange={(e) => setVoiceScript(e.target.value)}
                          placeholder='Exact words to be spoken — e.g. "Ace your FCPS exam. Ten thousand past papers, one app."'
                          disabled={generating}
                        />
                        <p className="text-2xs text-text-muted">
                          Spoken verbatim and trimmed to the video's length. It replaces the clip's own sound.
                        </p>
                      </>
                    ) : (
                      <p className="rounded-lg border border-dashed border-border bg-surface-alt/40 px-3 py-2 text-2xs text-text-muted">
                        The narration is written automatically from your video prompt after the clip renders — put
                        everything (scene, mood, what to say about the app) in the one prompt above. Sized to fit the
                        video's length.
                      </p>
                    )}
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Platform preset pills (none for voiceover) */}
          {presets.length > 0 && (
            <div className="mb-3 flex flex-wrap gap-1.5">
              {presets.map((p) => (
                <button
                  key={p.key}
                  onClick={() => setPreset(preset === p.key ? null : p.key)}
                  title={p.hint}
                  className={clsx(
                    'rounded-full border px-3 py-1 text-xs font-medium transition-colors',
                    preset === p.key
                      ? 'border-primary/60 bg-primary-soft text-primary'
                      : 'border-border bg-surface text-text-secondary hover:border-border-strong hover:text-text-primary',
                  )}
                >
                  {p.label}
                  {p.aspectRatio && <span className="ml-1 opacity-60">{p.aspectRatio}</span>}
                </button>
              ))}
            </div>
          )}

          <Textarea
            rows={4}
            value={prompt}
            onChange={(e) => setPrompt(e.target.value)}
            placeholder={promptPlaceholder[kind]}
            disabled={generating}
          />

          <div className="mt-3 flex items-center justify-between gap-2">
            {/* The voiceover script is spoken verbatim — "enhancing" it would change the words. */}
            {kind !== 'audio' ? (
              <EnhancePromptButton
                value={prompt}
                onChange={setPrompt}
                context={
                  kind === 'image' ? 'AI Studio image generation brief'
                  : kind === 'video' ? 'AI Studio video generation brief'
                  : 'AI Studio social copy brief'
                }
                disabled={generating}
              />
            ) : <span />}
            <Button
              onClick={generate}
              loading={generating}
              disabled={kind === 'audio' && elevenLabsMissing}
              iconLeft={<Wand2 size={15} />}
            >
              Generate {KIND_META[kind].noun}
            </Button>
          </div>
        </Card>

        {/* Right column — live status, result, or an idle placeholder. */}
        <div className="space-y-6">
        {/* In-flight / result card */}
        {generating && (
          <Card className="flex items-center gap-3">
            <Loader2 size={18} className="animate-spin text-primary" />
            <div>
              <div className="text-sm font-semibold text-text-primary">
                {postStage ?? `Generating ${KIND_META[kind].noun}…`}
              </div>
              <div className="text-xs text-text-muted">
                {kind === 'video'
                  ? 'Video rendering usually takes 2-6 minutes. It keeps rendering server-side — you can find it in the history below later.'
                  : kind === 'image' ? 'Usually takes 10-30 seconds.'
                  : kind === 'audio' ? 'Usually takes a few seconds.'
                  : 'Just a moment.'}
              </div>
            </div>
          </Card>
        )}

        {result && result.status === 'succeeded' && (
          <Card>
            <SectionHeader
              title="Result"
              subtitle={result.platformPreset ? PRESET_LABELS[result.platformPreset] : undefined}
              action={
                result.kind !== 'copy' && result.assetUrl ? (
                  <Button
                    variant="secondary"
                    size="sm"
                    iconLeft={<Download size={13} />}
                    onClick={() => downloadAsset(result.assetUrl!, `ai-studio-${result.generationUid}.${KIND_META[result.kind as CreateKind].ext}`)}
                  >
                    Download
                  </Button>
                ) : result.kind === 'copy' && result.copyText ? (
                  <Button
                    variant="secondary"
                    size="sm"
                    iconLeft={<CopyIcon size={13} />}
                    onClick={() => copyToClipboard(result.copyText!)}
                  >
                    Copy text
                  </Button>
                ) : undefined
              }
            />
            {result.kind === 'image' && result.assetUrl ? (
              <div className="flex justify-center rounded-xl border border-border bg-surface-alt/40 p-4">
                <img
                  src={result.assetUrl}
                  alt={result.prompt}
                  className="max-h-[520px] w-auto max-w-full rounded-lg shadow-soft"
                />
              </div>
            ) : result.kind === 'video' && result.assetUrl ? (
              <div className="flex justify-center rounded-xl border border-border bg-black/90 p-4">
                <video
                  src={result.assetUrl}
                  controls
                  playsInline
                  className="max-h-[520px] w-auto max-w-full rounded-lg"
                />
              </div>
            ) : result.kind === 'audio' && result.assetUrl ? (
              <div className="rounded-xl border border-border bg-surface-alt/40 p-4">
                <audio src={result.assetUrl} controls className="w-full" />
              </div>
            ) : (
              <div className="rounded-xl border border-border bg-surface-alt/40 p-4">
                <p className="whitespace-pre-wrap text-sm leading-relaxed text-text-primary">
                  {result.copyText}
                </p>
              </div>
            )}
          </Card>
        )}

        {result && result.status === 'failed' && (
          <Card className="flex items-start gap-3 border-danger/40">
            <AlertCircle size={18} className="mt-0.5 shrink-0 text-danger" />
            <div>
              <div className="text-sm font-semibold text-text-primary">Generation failed</div>
              <div className="mt-0.5 text-xs text-text-muted">{result.error ?? 'Unknown error'}</div>
            </div>
          </Card>
        )}

        {/* Idle placeholder — keeps the right column from collapsing empty. */}
        {!generating && !result && (
          <Card className="flex min-h-[320px] flex-col items-center justify-center gap-2 text-center">
            <span className="flex h-12 w-12 items-center justify-center rounded-2xl bg-primary-soft text-primary">
              <Wand2 size={22} />
            </span>
            <div className="text-sm font-semibold text-text-primary">Your result appears here</div>
            <p className="max-w-xs text-xs text-text-muted">
              Write a prompt on the left and hit generate. Images and voiceovers return in seconds; videos take a few minutes.
            </p>
          </Card>
        )}
        </div>{/* /right column */}
        </div>

        {/* History */}
        <div>
          <div className="mb-3 flex items-center justify-between">
            <h2 className="font-display text-base font-semibold tracking-tight text-text-primary">History</h2>
            <div className="flex gap-1 rounded-lg bg-surface-alt p-1">
              {(['image', 'video', 'audio', 'copy'] as const).map((k) => (
                <button
                  key={k}
                  onClick={() => { setHistoryKind(k); setPage(1); }}
                  className={clsx(
                    'rounded-md px-3 py-1 text-xs font-semibold transition-colors',
                    historyKind === k
                      ? 'bg-surface text-text-primary shadow-soft'
                      : 'text-text-muted hover:text-text-primary',
                  )}
                >
                  {KIND_META[k].historyLabel}
                </button>
              ))}
            </div>
          </div>

          {!history ? (
            <Card className="flex items-center justify-center py-12">
              <Loader2 size={18} className="animate-spin text-text-muted" />
            </Card>
          ) : history.items.length === 0 ? (
            <EmptyState
              icon={<Wand2 size={26} />}
              title={`No ${KIND_META[historyKind].historyLabel.toLowerCase()} yet`}
              description="Your generations will appear here so you can re-download and reuse them any time."
            />
          ) : historyKind === 'image' || historyKind === 'video' ? (
            <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-4">
              {history.items.map((g) => (
                <div
                  key={g.generationUid}
                  onMouseLeave={() => { if (useMenuFor === g.generationUid) setUseMenuFor(null); }}
                  className="group relative overflow-hidden rounded-xl border border-border bg-surface"
                >
                  {g.source === 'combined' && (
                    <span className="absolute left-1.5 top-1.5 z-10 inline-flex items-center gap-0.5 rounded bg-primary/85 px-1.5 py-0.5 text-[9px] font-semibold uppercase text-on-primary">
                      <Wand2 size={9} /> Combined
                    </span>
                  )}
                  {g.status === 'succeeded' && g.assetUrl ? (
                    g.kind === 'video' ? (
                      // ImageKit renders a still frame from the video (/ik-thumbnail.jpg,
                      // ~1s in to skip a black opening frame) — avoids the black <video> flash.
                      <div className="relative aspect-square w-full bg-black">
                        <img
                          src={g.assetUrl.includes('ik.imagekit.io') ? `${g.assetUrl}/ik-thumbnail.jpg?tr=so-1` : g.assetUrl}
                          alt={g.prompt}
                          loading="lazy"
                          className="h-full w-full object-cover"
                          onError={(e) => { (e.currentTarget as HTMLImageElement).style.display = 'none'; }}
                        />
                        <span className="absolute bottom-1.5 right-1.5 rounded-full bg-black/60 p-1 text-white"><Play size={11} /></span>
                      </div>
                    ) : (
                      <img
                        src={g.thumbnailUrl ?? g.assetUrl}
                        alt={g.prompt}
                        loading="lazy"
                        className="aspect-square w-full object-cover"
                      />
                    )
                  ) : (
                    <div className="flex aspect-square w-full flex-col items-center justify-center gap-1.5 bg-surface-alt px-3 text-center">
                      {g.status === 'failed' ? (
                        <>
                          <AlertCircle size={18} className="text-danger" />
                          <span className="text-2xs text-text-muted line-clamp-2">{g.error ?? 'Failed'}</span>
                        </>
                      ) : (
                        <>
                          <Clock size={18} className="animate-pulse text-text-muted" />
                          <span className="text-2xs text-text-muted">
                            {g.kind === 'video' ? 'Rendering… (takes minutes)' : 'Processing…'}
                          </span>
                        </>
                      )}
                    </div>
                  )}

                  {/* "Use asset" menu */}
                  {useMenuFor === g.generationUid && g.assetUrl && (
                    <div className="absolute inset-x-2 bottom-11 z-10 overflow-hidden rounded-lg border border-border bg-surface shadow-elevated">
                      <button
                        onClick={() => { setUseMenuFor(null); applyToTemplate(g); }}
                        className="flex w-full items-center gap-2 px-3 py-2 text-left text-xs text-text-primary hover:bg-surface-alt"
                      >
                        <LayoutTemplate size={13} className="shrink-0 text-text-muted" /> WhatsApp template header
                      </button>
                      <button
                        onClick={() => { setUseMenuFor(null); applyToBroadcast(g); }}
                        className="flex w-full items-center gap-2 px-3 py-2 text-left text-xs text-text-primary hover:bg-surface-alt"
                      >
                        <Send size={13} className="shrink-0 text-text-muted" /> Broadcast (copies URL)
                      </button>
                      <button
                        onClick={() => { setUseMenuFor(null); copyToClipboard(g.assetUrl!, 'Asset URL copied'); }}
                        className="flex w-full items-center gap-2 px-3 py-2 text-left text-xs text-text-primary hover:bg-surface-alt"
                      >
                        <LinkIcon size={13} className="shrink-0 text-text-muted" /> Copy URL
                      </button>
                    </div>
                  )}

                  {/* Hover actions */}
                  <div className="absolute inset-x-0 bottom-0 flex items-center justify-between gap-2 bg-gradient-to-t from-black/70 to-transparent p-2 opacity-0 transition-opacity group-hover:opacity-100">
                    <span className="truncate text-2xs text-white/90" title={g.prompt}>{g.prompt}</span>
                    <span className="flex shrink-0 gap-1">
                      {g.status === 'succeeded' && g.assetUrl && (
                        <>
                          <button
                            onClick={() => setUseMenuFor(useMenuFor === g.generationUid ? null : g.generationUid)}
                            className={clsx(
                              'rounded-md p-1.5 text-white backdrop-blur hover:bg-white/30',
                              useMenuFor === g.generationUid ? 'bg-white/30' : 'bg-white/15',
                            )}
                            title="Use this asset"
                          >
                            <Share2 size={13} />
                          </button>
                          <button
                            onClick={() => downloadAsset(g.assetUrl!, `ai-studio-${g.generationUid}.${KIND_META[g.kind as CreateKind].ext}`)}
                            className="rounded-md bg-white/15 p-1.5 text-white backdrop-blur hover:bg-white/30"
                            title="Download"
                          >
                            <Download size={13} />
                          </button>
                        </>
                      )}
                      <button
                        onClick={() => setDel(g)}
                        className="rounded-md bg-white/15 p-1.5 text-white backdrop-blur hover:bg-danger"
                        title="Delete"
                      >
                        <Trash2 size={13} />
                      </button>
                    </span>
                  </div>
                </div>
              ))}
            </div>
          ) : historyKind === 'audio' ? (
            <div className="space-y-2">
              {history.items.map((g) => (
                <Card key={g.generationUid} className="group">
                  <div className="flex items-start justify-between gap-3">
                    <div className="min-w-0 flex-1">
                      <div className="flex items-center gap-2 text-2xs text-text-muted">
                        <span>{new Date(g.createdAt).toLocaleString()}</span>
                      </div>
                      {g.status === 'failed' ? (
                        <p className="mt-2 text-xs text-danger">{g.error ?? 'Failed'}</p>
                      ) : g.status === 'succeeded' && g.assetUrl ? (
                        <audio src={g.assetUrl} controls className="mt-2 w-full max-w-md" />
                      ) : (
                        <p className="mt-2 flex items-center gap-1.5 text-xs text-text-muted">
                          <Clock size={13} className="animate-pulse" /> Processing…
                        </p>
                      )}
                      <p className="mt-2 truncate text-2xs text-text-muted" title={g.prompt}>
                        Script: {g.prompt}
                      </p>
                    </div>
                    <span className="flex shrink-0 gap-1 opacity-0 transition-opacity group-hover:opacity-100">
                      {g.status === 'succeeded' && g.assetUrl && (
                        <button
                          onClick={() => downloadAsset(g.assetUrl!, `ai-studio-${g.generationUid}.mp3`)}
                          className="rounded-md border border-border p-1.5 text-text-muted hover:bg-surface-alt hover:text-text-primary"
                          title="Download"
                        >
                          <Download size={13} />
                        </button>
                      )}
                      <button
                        onClick={() => setDel(g)}
                        className="rounded-md border border-border p-1.5 text-text-muted hover:bg-danger-soft hover:text-danger"
                        title="Delete"
                      >
                        <Trash2 size={13} />
                      </button>
                    </span>
                  </div>
                </Card>
              ))}
            </div>
          ) : (
            <div className="space-y-2">
              {history.items.map((g) => (
                <Card key={g.generationUid} className="group">
                  <div className="flex items-start justify-between gap-3">
                    <div className="min-w-0 flex-1">
                      <div className="flex items-center gap-2 text-2xs text-text-muted">
                        {g.platformPreset && (
                          <span className="rounded-full bg-primary-soft px-2 py-0.5 font-medium text-primary">
                            {PRESET_LABELS[g.platformPreset] ?? g.platformPreset}
                          </span>
                        )}
                        <span>{new Date(g.createdAt).toLocaleString()}</span>
                      </div>
                      {g.status === 'failed' ? (
                        <p className="mt-2 text-xs text-danger">{g.error ?? 'Failed'}</p>
                      ) : (
                        <p className="mt-2 whitespace-pre-wrap text-sm leading-relaxed text-text-primary">
                          {g.copyText}
                        </p>
                      )}
                      <p className="mt-2 truncate text-2xs text-text-muted" title={g.prompt}>
                        Prompt: {g.prompt}
                      </p>
                    </div>
                    <span className="flex shrink-0 gap-1 opacity-0 transition-opacity group-hover:opacity-100">
                      {g.copyText && (
                        <button
                          onClick={() => copyToClipboard(g.copyText!)}
                          className="rounded-md border border-border p-1.5 text-text-muted hover:bg-surface-alt hover:text-text-primary"
                          title="Copy text"
                        >
                          <CopyIcon size={13} />
                        </button>
                      )}
                      <button
                        onClick={() => setDel(g)}
                        className="rounded-md border border-border p-1.5 text-text-muted hover:bg-danger-soft hover:text-danger"
                        title="Delete"
                      >
                        <Trash2 size={13} />
                      </button>
                    </span>
                  </div>
                </Card>
              ))}
            </div>
          )}

          {history && history.total > HISTORY_LIMIT && (
            <Pagination page={page} total={history.total} limit={HISTORY_LIMIT} onChange={setPage} />
          )}
        </div>
      </div>

      <ConfirmDialog
        open={!!del}
        title="Delete generation?"
        message={del?.kind === 'copy'
          ? 'This copy will be removed from your history. This cannot be undone.'
          : 'The asset will be removed from your history and storage. This cannot be undone.'}
        danger
        onConfirm={onDelete}
        onCancel={() => setDel(null)}
      />
    </>
  );
}
